/**
 * The typed docs nav tree — the single source of truth the whole /docs
 * surface derives from: sidebar, breadcrumbs, prev/next pager, ⌘K search
 * index, sitemap-ish metadata, the sidebar IA stamp, and /llms.txt.
 * Statuses and ETAs live HERE (once); every tag/sash/strip derives.
 */

export type DocStatus = 'live' | 'stub' | 'roadmap';

export interface NavItem {
  id: string;
  label: string;
  /** URL path under /docs ('' = the docs home). */
  slug: string;
  status: DocStatus;
  /** Roadmap ETA, e.g. 'Q1 2027'. Data, not copy — edit once, moves everywhere. */
  eta?: string;
  /** Search haystack extras for the ⌘K index. */
  kws: string;
}

export interface NavGroup {
  group: string;
  items: NavItem[];
}

export const API_BASE_DEFAULT = 'https://api.rii.link/v1';

/** Rewrites every code pane at once (staging docs for free). */
export const API_BASE = process.env.NEXT_PUBLIC_DOCS_API_BASE ?? API_BASE_DEFAULT;

/** false = shipped-only IA: roadmap items drop from nav, search, popular
 *  strip, pager and the home honesty strip. */
export const SHOW_ROADMAP = true;

export const NAV: NavGroup[] = [
  { group: 'GETTING STARTED', items: [
    { id: 'home', label: 'Introduction', slug: '', status: 'live', kws: 'overview start docs home map' },
    { id: 'qs-dash', label: 'Quickstart — Dashboard', slug: 'quickstart/dashboard', status: 'live', kws: 'first link no code ui three minutes' },
    { id: 'qs-api', label: 'Quickstart — API', slug: 'quickstart/api', status: 'live', kws: 'curl create link code token key' },
  ]},
  { group: 'GUIDES', items: [
    { id: 'g-routing', label: 'Routing rules', slug: 'guides/routing-rules', status: 'stub', kws: 'country device language time geo smart conditions' },
    { id: 'g-domains', label: 'Custom domains', slug: 'guides/custom-domains', status: 'stub', kws: 'cname dns host connect verify' },
    { id: 'g-qr', label: 'QR codes', slug: 'guides/qr-codes', status: 'stub', kws: 'qr png svg print scan' },
    { id: 'g-analytics', label: 'Analytics & UTM', slug: 'guides/analytics-utm', status: 'live', kws: 'clicks stats utm campaign referrer builder destination tags' },
    { id: 'g-bulk', label: 'Bulk import (CSV)', slug: 'guides/bulk-import', status: 'roadmap', eta: 'Q4 2026', kws: 'csv upload migrate batch import' },
  ]},
  { group: 'API REFERENCE', items: [
    { id: 'api-auth', label: 'Authentication', slug: 'api/authentication', status: 'live', kws: 'bearer token api key authorization scopes' },
    { id: 'api-links', label: 'Links', slug: 'api/links', status: 'live', kws: 'create link mapping slug routing shorten destination' },
    { id: 'api-domains', label: 'Domains', slug: 'api/domains', status: 'live', kws: 'hosts dns verify cname txt records' },
    { id: 'api-qr', label: 'QR codes', slug: 'api/qr-codes', status: 'live', kws: 'qr render png svg cdn' },
    { id: 'api-analytics', label: 'Analytics', slug: 'api/analytics', status: 'live', kws: 'stats clicks sse stream events live' },
    { id: 'api-export', label: 'Export', slug: 'api/export', status: 'live', kws: 'download csv json nginx caddy takeout backup request log clicks' },
    { id: 'api-errors', label: 'Errors & rate limits', slug: 'api/errors', status: 'live', kws: '429 envelope retry codes limit 401 404 409' },
    { id: 'api-webhooks', label: 'Webhooks', slug: 'api/webhooks', status: 'roadmap', eta: 'Q1 2027', kws: 'events delivery callback push notify' },
  ]},
  // No Node/Python SDK packages exist — the group holds the two real ways in.
  { group: 'CLIENTS', items: [
    { id: 'sdk-http', label: 'cURL / raw HTTP', slug: 'sdks/http', status: 'live', kws: 'rest http requests shell fetch node python' },
    { id: 'sdk-mcp', label: 'MCP server', slug: 'sdks/mcp', status: 'live', kws: 'model context protocol agents claude cursor remote install' },
  ]},
  { group: 'SELF-SERVE OPS', items: [
    { id: 'ops-keys', label: 'API keys & rotation', slug: 'ops/api-keys', status: 'live', kws: 'rotate revoke security secrets create environment access owner workspace' },
    { id: 'ops-export', label: 'Data export', slug: 'ops/data-export', status: 'live', kws: 'download csv json nginx caddy takeout backup settings request log retention' },
    { id: 'ops-migrate', label: 'Migrating from Bitly', slug: 'ops/migrate-from-bitly', status: 'stub', kws: 'import 301 redirect switch move' },
  ]},
  { group: 'SUPPORT', items: [
    { id: 'sup-status', label: 'Status & uptime', slug: 'support/status', status: 'stub', kws: 'incident sla operational outage' },
    { id: 'sup-changelog', label: 'Changelog', slug: 'support/changelog', status: 'stub', kws: 'release notes new shipped' },
    { id: 'sup-contact', label: 'Contact', slug: 'support/contact', status: 'stub', kws: 'help email support human' },
  ]},
];

export interface FlatNavItem extends NavItem {
  group: string;
}

export const flatAll = (): FlatNavItem[] =>
  NAV.flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })));

/** The visible tree — roadmap items drop when SHOW_ROADMAP is off. */
export const flatVisible = (): FlatNavItem[] =>
  flatAll().filter((i) => SHOW_ROADMAP || i.status !== 'roadmap');

export const findById = (id: string): FlatNavItem | undefined =>
  flatAll().find((i) => i.id === id);

export const findBySlug = (slug: string): FlatNavItem | undefined =>
  flatAll().find((i) => i.slug === slug);

/** Un-prefixed site path for a docs page — proxy.ts adds the locale. */
export const docsPath = (id: string): string => {
  const item = findById(id);
  return item && item.slug ? `/docs/${item.slug}` : '/docs';
};

/** 'Q4 2026' → 'Q4 ’26' — the amber mono tag grammar. */
export const etaTag = (eta?: string): string | null =>
  eta ? eta.toUpperCase().replace(' 2026', ' ’26').replace(' 2027', ' ’27') : null;

export const prevNext = (id: string): { prev: FlatNavItem | null; next: FlatNavItem | null } => {
  const flat = flatVisible();
  const i = flat.findIndex((it) => it.id === id);
  return {
    prev: i > 0 ? flat[i - 1] : null,
    next: i >= 0 && i < flat.length - 1 ? flat[i + 1] : null,
  };
};

/** Derived sidebar footer stamp — counts, never hand-maintained. */
export const iaStamp = (): string => {
  const groups = NAV.filter((g) => g.items.some((i) => SHOW_ROADMAP || i.status !== 'roadmap'));
  return `IA V1 · ${groups.length} GROUPS · ${flatVisible().length} PAGES — STATUSES: LIVE / STUB / ROADMAP`;
};
